import { List } from "immutable";
import { Observable, observable } from "micro-observables";
import { Signal } from "micro-signals";
import { Page, PaginationOptions } from "@utils/page";
import { fromPageAndContent, PaginatedData } from "@core/paginatedData";

export interface PaginatedStore<T, Extra = unknown, Args extends unknown[] = []> {
	paginatedItems: Observable<PaginatedData<T, Extra> | null>;
	onFetch: Signal<{ args: Args; result: Page<unknown> }>;
	fetch: (...args: Args) => Promise<void>;
	fetchMore: (...args: Args) => Promise<void>;
	fetching: Observable<boolean>;
}

export class SimplePaginatedStore<T, Extra, Args extends unknown[] = []> implements PaginatedStore<T, Extra, Args> {
	paginatedItems = observable<PaginatedData<T, Extra> | null>(null);
	fetching = observable(false);
	onFetch = new Signal<{ args: Args; result: Page<unknown> }>();

	constructor(
		private readonly _fetchList: (page: PaginationOptions, ...args: Args) => Promise<Page<T, Extra>>,
		private readonly size = 20
	) {}

	async fetch(...args: Args): Promise<void> {
		this.fetching.set(true);
		const result = await this._fetchList({ page: 0, size: this.size }, ...args);
		this.onFetch.dispatch({ args, result });
		this.paginatedItems.set(fromPageAndContent(result, List(result.content)));
		this.fetching.set(false);
	}

	async fetchMore(...args: Args): Promise<void> {
		if (this.fetching.get()) {
			return;
		}
		const currentItems = this.paginatedItems.get();
		if (!currentItems) {
			return this.fetch(...args);
		}
		const newItems = await this._fetchList({ page: currentItems.page + 1, size: this.size }, ...args);
		this.paginatedItems.set(fromPageAndContent(newItems, currentItems.content.concat(newItems.content)));
	}
}
